import React, { useEffect, useState, useRef } from "react";

import OverviewTab     from "./tabs/OverviewTab";
import SensorsTab      from "./tabs/SensorsTab";
import RelayControlTab from "./tabs/RelayControlTab";
import AdditionalTab   from "./tabs/AdditionalTab";
import DeviceConfigurationTab from "./tabs/DeviceConfigurationTab";

/* ─── Config ─── */
const WS_URL =
  process.env.REACT_APP_WS_URL ||
  "wss://ztw46d04q3.execute-api.us-east-1.amazonaws.com/production";

const API = process.env.REACT_APP_API_URL || "/api";

const normalizeTelemetryKeys = (data) => {
  if (Array.isArray(data)) return data.map(normalizeTelemetryKeys);
  if (!data || typeof data !== "object") return data;
  return Object.entries(data).reduce((normalized, [rawKey, value]) => {
    const key = String(rawKey).trim();
    if (!key || (key in normalized && rawKey !== key)) return normalized;
    normalized[key] = normalizeTelemetryKeys(value);
    return normalized;
  }, {});
};

const TELEMETRY_KEY_PATTERN = /^(?:d|i)\d+$/i;
const NAMED_TELEMETRY_KEYS = new Set(["temperature", "humidity", "pressure", "aqi", "co2", "pm25", "pm10", "voc", "voltage", "current", "power"]);

const hasDirectTelemetryValues = (data) => Object.keys(data || {}).some((key) => {
  const normalized = String(key).trim().toLowerCase();
  return TELEMETRY_KEY_PATTERN.test(normalized) || NAMED_TELEMETRY_KEYS.has(normalized);
});

const extractTelemetryData = (message) => {
  let current = normalizeTelemetryKeys(message);
  for (let depth = 0; depth < 4; depth += 1) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return {};
    if (hasDirectTelemetryValues(current)) break;
    const nested = current.payload && typeof current.payload === "object"
      ? current.payload
      : current.data && typeof current.data === "object"
        ? current.data
        : null;
    if (!nested) break;
    current = nested;
  }
  return current && typeof current === "object" && !Array.isArray(current) ? current : {};
};

const hasTelemetryValues = (data) => hasDirectTelemetryValues(extractTelemetryData(data));

const toTimestamp = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

/* ─── Tab config ─── */
const TABS = [
  { key: "overview",   label: "Overview"      },
  { key: "sensors",    label: "Sensors"       },
  { key: "relay",      label: "Relay Control" },
  { key: "additional", label: "Reports"       },
];

export default function DeviceStandalone({ device, onBack }) {
  const [tab,       setTab]       = useState("overview");
  const [liveData,  setLiveData]  = useState({});
  // The dashboard's WebSocket being connected does not prove the physical
  // device is online. Health comes from fresh device telemetry instead.
  const [deviceOnline, setDeviceOnline] = useState(
    device?.isOnline === true || device?.status === "online"
  );
  const [deviceConfig, setDeviceConfig] = useState(null);
  const isSuperAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "super_admin"; } catch { return false; } })();

  const socketRef    = useRef(null);
  const reconnectRef = useRef(null);
  const pendingRelayRef = useRef({});

  /* ─────────────────────────────────────
     Lock body scroll while overlay is open
  ───────────────────────────────────── */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  /* ─────────────────────────────────────
     SEED liveData from last known reading
  ───────────────────────────────────── */
  useEffect(() => {
    const seed = device?.last_dynamo_data || device?.liveData;
    if (seed && Object.keys(seed).length > 0) {
      setLiveData(seed);
    }
  // Seed only when navigating to a different device.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.device_uid]);

  useEffect(() => {
    setDeviceOnline(device?.isOnline === true || device?.status === "online");
  }, [device?.device_uid, device?.isOnline, device?.status]);

  /* ─────────────────────────────────────
     AWS API GATEWAY WEBSOCKET
  ───────────────────────────────────── */
  useEffect(() => {
    if (!device?.device_uid) return;

    const connectWS = () => {
      const socket = new WebSocket(WS_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify({
          action:   "subscribe",
          deviceId: device.device_uid,
        }));
      };

      socket.onmessage = (event) => {
        try {
          const msg = normalizeTelemetryKeys(JSON.parse(event.data));
          const msgDeviceId = msg.deviceId || msg.device_uid || msg.deviceUid || msg.data?.deviceId || msg.data?.device_uid || msg.data?.deviceUid;
          const msgData = extractTelemetryData(msg);

          if (String(msgDeviceId || "").toUpperCase() !== String(device.device_uid).toUpperCase()) return;
          if (msg.type && msg.type !== "deviceData") return;
          if (!hasTelemetryValues(msgData)) return;

          const now = Date.now();
          setDeviceOnline(true);
          setLiveData((current) => {
            const next = { ...current, ...msgData, ts: now };

            Object.entries(pendingRelayRef.current).forEach(([relay, pending]) => {
              if (!pending || now - pending.ts > 90000) {
                delete pendingRelayRef.current[relay];
                return;
              }

              const reported = msgData?.[relay] ?? msgData?.[String(relay).toLowerCase()] ?? msgData?.[String(relay).toUpperCase()];
              const normalizedReported = String(reported ?? "").trim().toLowerCase();
              const reportedState = reported === 1 || reported === true || reported === "1" || normalizedReported === "true" || normalizedReported === "on" ? 1 : 0;

              if (reported !== undefined) {
                next[`${relay}_desired`] = pending.state;
                next[`${relay}_command_ts`] = pending.ts;
                if (reportedState === pending.state) delete pendingRelayRef.current[relay];
                return;
              }

              next[relay] = pending.state;
              next[`${relay}_desired`] = pending.state;
              next[`${relay}_command_ts`] = pending.ts;
            });

            return next;
          });

          fetch(`${API}/iot/push`, {
            method:  "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body:    JSON.stringify({
              device_uid: device.device_uid,
              data:       msgData,
            }),
          }).catch(() => {});

        } catch (err) {
          console.error("WS parse error:", err);
        }
      };

      socket.onerror = () => socket.close();

      socket.onclose = () => {
        socketRef.current    = null;
        reconnectRef.current = setTimeout(connectWS, 3000);
      };
    };

    connectWS();

    return () => {
      if (socketRef.current)    socketRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [device?.device_uid]);

  /* Refresh from the authenticated device API so a missed WebSocket frame or
     backend restart cannot leave sensor and relay state frozen indefinitely. */
  useEffect(() => {
    if (!device?.device_uid) return;
    let cancelled = false;

    const refreshDevice = async () => {
      try {
        const response = await fetch(`${API}/devices/uid/${encodeURIComponent(device.device_uid)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!response.ok) return;
        const refreshed = await response.json();
        if (!cancelled) setDeviceOnline(refreshed?.isOnline === true || refreshed?.status === "online");
        const telemetry = extractTelemetryData(refreshed?.liveData || refreshed?.last_dynamo_data || {});
        if (cancelled || !hasTelemetryValues(telemetry)) return;

        const incomingTs = toTimestamp(telemetry.ts || refreshed?.last_seen);
        setLiveData((current) => {
          const currentTs = toTimestamp(current?.ts);
          if (incomingTs && currentTs && incomingTs < currentTs) return current;
          return { ...current, ...telemetry, ts: incomingTs || currentTs || Date.now() };
        });
      } catch {
        // The WebSocket remains the primary path; the refresh is recovery only.
      }
    };

    refreshDevice();
    const timer = setInterval(refreshDevice, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [device?.device_uid]);

  /* ─── Relay control ─── */
  const handleRelayControl = async (relayKey, state, options = {}) => {
    const response = await fetch(`${API}/relay/control`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        device_uid: device.device_uid,
        relay: relayKey,
        state,
        always_on: options.always_on === true,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      throw new Error(result.message || "Relay command failed");
    }

    const confirmedRelay = result.relay || relayKey;
    const confirmedState = Number(result.state ?? state);
    pendingRelayRef.current[confirmedRelay] = {
      state: confirmedState,
      ts: Date.now(),
    };
    setLiveData((current) => ({
      ...current,
      [confirmedRelay]: confirmedState,
      [`${confirmedRelay}_desired`]: confirmedState,
      [`${confirmedRelay}_command_ts`]: Date.now(),
    }));

    return result;
  };

  const sensors    = deviceConfig?.sensors || device.sensors || [];
  const relays     = deviceConfig?.relays  || device.relays  || [];
  const lastUpdate = liveData?.ts
    ? new Date(liveData.ts).toLocaleTimeString()
    : null;

  return (
    /*
     * 🔥 fixed inset-0 z-[9999] — covers the sidebar AND topbar completely.
     *    overflow-y-auto — the content inside scrolls, not the page behind.
     *    No routing change needed; this overlays everything.
     */
    <div className="fixed inset-0 z-[9999] bg-gray-50 overflow-y-auto flex flex-col">

      {/* ── Device header ── */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">

          <div className="flex items-center gap-4">
            {/* Back / Exit button */}
            <button
              onClick={onBack}
              className="flex items-center gap-1.5 text-[12px] text-gray-400 hover:text-gray-700 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Devices
            </button>

            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-[15px] font-semibold text-gray-900">
                  {device.product_name || device.product_type || "Device"}
                </h1>
                <span className="text-[11px] font-mono text-gray-400 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded">
                  {device.device_uid}
                </span>
              </div>
              {device.company_name && (
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {device.company_name}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {lastUpdate && (
              <span className="text-[11px] text-gray-400 hidden sm:block">
                Updated{" "}
                <span className="font-mono font-medium text-gray-600">
                  {lastUpdate}
                </span>
              </span>
            )}

            {/* Live / Offline pill */}
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${
                deviceOnline
                  ? "bg-green-50 border-green-200 text-green-700"
                  : "bg-red-50 border-red-200 text-red-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  deviceOnline ? "bg-green-500 animate-pulse" : "bg-red-400"
                }`}
              />
              {deviceOnline ? "Online" : "Offline"}
            </div>

            {/* ✕ Exit button — alternative to the ← back link */}
            <button
              onClick={onBack}
              title="Close"
              className="ml-2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 flex">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`
                px-5 py-3.5 text-[13px] font-medium border-b-2 transition-colors
                ${tab === t.key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50"}
              `}
            >
              {t.label}
            </button>
          ))}
          {isSuperAdmin && <button onClick={() => setTab("configuration")} className={`px-5 py-3.5 text-[13px] font-medium border-b-2 transition-colors ${tab === "configuration" ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50"}`}>Device Configuration</button>}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="flex-1 max-w-7xl w-full mx-auto p-6">

        {/* Offline warning */}
        {!deviceOnline && tab !== "additional" && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-amber-500 text-base">⚠</span>
            <p className="text-[12px] text-amber-700 font-medium">
              Waiting for live data — device may be offline or not yet transmitting.
            </p>
          </div>
        )}

        {tab === "overview"   && (
          <OverviewTab liveData={liveData} sensors={sensors} relays={relays} device={device} />
        )}
        {tab === "sensors"    && (
          <SensorsTab liveData={liveData} sensors={sensors} />
        )}
        {tab === "relay"      && (
          <RelayControlTab
            device={device}
            relays={relays}
            liveData={liveData}
            onControl={handleRelayControl}
          />
        )}
        {tab === "additional" && (
          <AdditionalTab
            deviceId={device.device_uid}
            liveData={liveData}
            sensors={sensors}
          />
        )}
        {tab === "configuration" && isSuperAdmin && (
          <DeviceConfigurationTab device={device} onConfigUpdated={(config) => setDeviceConfig({ sensors: config.sensors || [], relays: config.relays || [] })} />
        )}
      </div>
    </div>
  );
}
